import { useCallback, useMemo } from 'react';
import {
  CustomCheckbox,
  SelectOption,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';

interface IssuerNotificationTableProps {
  issuerOptions: SelectOption<number>[];
  notifyIssuers: boolean;
  issuerUserIds: number[];
  onNotifyIssuersChange: (notifyIssuers: boolean) => void;
  onIssuerUserIdsChange: (issuerUserIds: number[]) => void;
}

export const IssuerNotificationTable = ({
  issuerOptions,
  notifyIssuers,
  issuerUserIds,
  onNotifyIssuersChange,
  onIssuerUserIdsChange,
}: IssuerNotificationTableProps) => {
  const isAllSelected =
    issuerOptions.length > 0 && issuerOptions.every(({ value }) => issuerUserIds.includes(value));
  const isSomeSelected = issuerOptions.some(({ value }) => issuerUserIds.includes(value));

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      onNotifyIssuersChange(checked);
      onIssuerUserIdsChange(checked ? issuerOptions.map(({ value }) => value) : []);
    },
    [issuerOptions, onIssuerUserIdsChange, onNotifyIssuersChange],
  );

  const handleToggleIssuer = useCallback(
    (issuerUserId: number, checked: boolean) => {
      onNotifyIssuersChange(true);
      onIssuerUserIdsChange(
        checked
          ? Array.from(new Set([...issuerUserIds, issuerUserId]))
          : issuerUserIds.filter(id => id !== issuerUserId),
      );
    },
    [issuerUserIds, onIssuerUserIdsChange, onNotifyIssuersChange],
  );

  const headItems = useMemo<TableHeadColumn[]>(
    () => [
      { title: 'Issuer', className: 'min-w-[220px]' },
      { title: 'Notify', className: 'w-[72px] min-w-[72px] px-0 text-center whitespace-nowrap' },
    ],
    [],
  );

  const items = useMemo<TableBodyColumn[]>(
    () =>
      issuerOptions.map(({ value, label }) => {
        const issuerName = typeof label === 'string' ? label : `Issuer ${value}`;
        const checked = notifyIssuers && issuerUserIds.includes(value);

        return {
          cells: [
            { cell: <span className="text-sm">{label}</span> },
            {
              cell: (
                <div className="flex justify-center" onClick={event => event.stopPropagation()}>
                  <TooltipNew
                    text={
                      checked ? 'Do not send email to this issuer' : 'Notify this issuer by email'
                    }
                    place="left"
                    offset={10}
                  >
                    <CustomCheckbox
                      checked={checked}
                      onChange={event => handleToggleIssuer(value, event.target.checked)}
                      aria-label={`Notify ${issuerName} by email`}
                    />
                  </TooltipNew>
                </div>
              ),
              className: 'px-0',
            },
          ],
        };
      }),
    [handleToggleIssuer, issuerOptions, issuerUserIds, notifyIssuers],
  );

  if (!issuerOptions.length) {
    return null;
  }

  return (
    <div className="w-full flex flex-col gap-3 pt-2">
      <div className="flex items-center justify-end gap-2 px-3 sm:px-6">
        <span className="text-sm leading-5 text-gray-900">Notify issuers by email</span>
        <CustomCheckbox
          className="select-none mr-0 leading-none"
          checked={notifyIssuers && isAllSelected}
          indeterminate={notifyIssuers && isSomeSelected && !isAllSelected}
          onChange={event => handleToggleAll(event.target.checked)}
          aria-label="Notify all issuers by email"
        />
      </div>
      <Table
        className="my-0"
        withRounded={false}
        withBorderX={false}
        headItems={headItems}
        items={items}
      />
    </div>
  );
};
